import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import {
  COMPANIES as CATALOG_COMPANIES,
  SiteDef,
  sitesForCompany,
} from '../profiles/dashboard/catalog'

export type CompanyOption = { value: string; label: string }
export type SiteOption = { value: string; label: string; type: string }
export type HorizonOption = { value: number; label: string }

export const COMPANIES: CompanyOption[] = CATALOG_COMPANIES.map((c) => ({
  value: c.id,
  label: c.name,
}))

export const HORIZONS: HorizonOption[] = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
]

const toSiteOption = (s: SiteDef): SiteOption => ({
  value: s.id,
  label: s.name,
  type: s.type,
})

type AiFilterContextValue = {
  company: CompanyOption
  setCompany: (c: CompanyOption) => void
  site: SiteOption
  setSite: (s: SiteOption) => void
  horizon: HorizonOption
  setHorizon: (h: HorizonOption) => void
  companies: CompanyOption[]
  sites: SiteOption[]
  horizons: HorizonOption[]
  siteDef: SiteDef
}

const AiFilterContext = createContext<AiFilterContextValue | null>(null)

export const AiFilterProvider = ({ children }: { children: ReactNode }) => {
  const [company, setCompanyState] = useState<CompanyOption>(COMPANIES[0])
  const companySites = useMemo(
    () => sitesForCompany(company.value).map(toSiteOption),
    [company.value]
  )
  const [site, setSite] = useState<SiteOption>(companySites[0])
  const [horizon, setHorizon] = useState<HorizonOption>(HORIZONS[2])

  const setCompany = useCallback((c: CompanyOption) => {
    setCompanyState(c)
    const next = sitesForCompany(c.value)
    setSite(toSiteOption(next[0]))
  }, [])

  const siteDef = useMemo(
    () =>
      sitesForCompany(company.value).find((s) => s.id === site.value) ||
      sitesForCompany(company.value)[0],
    [company.value, site.value]
  )

  const value = useMemo(
    () => ({
      company,
      setCompany,
      site,
      setSite,
      horizon,
      setHorizon,
      companies: COMPANIES,
      sites: companySites,
      horizons: HORIZONS,
      siteDef,
    }),
    [company, setCompany, site, horizon, companySites, siteDef]
  )

  return <AiFilterContext.Provider value={value}>{children}</AiFilterContext.Provider>
}

export const useAiFilters = () => {
  const ctx = useContext(AiFilterContext)
  if (!ctx) {
    throw new Error('useAiFilters must be used within AiFilterProvider')
  }
  return ctx
}

export const selectStyles = {
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: 42,
    borderColor: '#E4E6EF',
    borderRadius: 8,
    boxShadow: 'none',
    '&:hover': { borderColor: '#B5B5C3' },
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    borderRadius: 8,
    zIndex: 20,
  }),
}
